/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import { DEFAULT_MARKETPLACE, PUSH_TO_SB_ENABLE, SAVE_ENABLE } from './constants';
import marketplacesAPI from '../mocks/marketplaces.json';

const TYPE = 'type';
const NUMBER_TYPE = 'Numbers';
const S_LETTER_TYPE = 'Single letters';
const D_LETTER_TYPE = 'Double letters';
const T_LETTER_TYPE = 'Triple letters';
const singleLetterPattern = /^[a-zA-Z]{1}$/;
const doubleLetterPattern = /^[a-zA-Z]{2}$/;
const tripleLetterPattern = /^[a-zA-Z]{3}$/;
const onlyNumberPattern = /^\d+$/;

/**
 * Returns only numbers
 * @param {string} value
 * @returns {number}
 */
export const onlyNumbers = (value = '') => {
  const pattern = /\D/g;
  return Number(value.replace(pattern, ''));
};

/**
 * Format numbers with decimal
 * @param {string|number} value
 * @param {number} decimals
 * @param {boolean} allowEmpty
 * @returns {number}
 */
export const formatFloat = (value = '', decimals = 2, allowEmpty) => {
  const pattern = /(\d)(?=(\d{3})+(?!\d))/g;

  if (allowEmpty && value === '') return '';

  return Number(value)
    .toFixed(decimals)
    .replace(pattern, '$1');
};

/**
 * dateFormatter Returns a formatted date with the description of the time has been passed or when it will happen
 * @param {string} date Datetime to be formatted
 * @param {boolean} past Boolean to know if you want the past time or incoming
 */
export const dateFormatter = (date, past = true) => {
  if (date === null) {
    return 'no dates found';
  }
  const currDate = moment(new Date(date.concat(' UTC')));
  const now = moment(new Date());
  let duration = new Date();
  if (past) {
    duration = moment.duration(now.diff(currDate));
  } else {
    duration = moment.duration(currDate.diff(now));
  }
  function splitTime(numberOfHours) {
    const days = Math.floor(numberOfHours / 24);
    const remainder = numberOfHours % 24;
    const hours = Math.floor(remainder);
    const minutes = Math.floor(60 * (remainder - hours));
    return `${JSON.stringify(days)} day ${JSON.stringify(hours)} hr ${JSON.stringify(minutes)} minutes`;
  }
  function timeConvert(info) {
    const hrs = parseInt(Number(info), 0);
    const min = Math.round((Number(info) - hrs) * 60);
    if (hrs > 24) {
      return splitTime(info);
    }
    if (hrs === 0) {
      if (min < 2) {
        return 'Just Now';
      }
      return `${JSON.stringify(min)} minutes`;
    }
    return `${JSON.stringify(hrs)} hr ${JSON.stringify(min)} minutes`;
  }
  return timeConvert(duration.asHours().toFixed(2));
};

/**
 *  Handles market place include exclude selections
 * @param {object} marketPlace setup of market places
 * @param {object} current current market place selected
 * @return {object} returns market place updated
 */
export const handleMarketplace = (marketPlace, current) => {
  const [key, value] = current.split('_');
  const prefix = value[0];
  const newMarketplace = { ...marketPlace };
  const hasPLus = Object.keys(newMarketplace).filter(attr => newMarketplace[attr].value.split('_')[1][0] === '+');

  if (hasPLus.length) {
    newMarketplace[hasPLus[0]].value = `${newMarketplace[hasPLus[0]].name}_${newMarketplace[hasPLus[0]].name}`;
  }

  if (prefix === '+') {
    Object.keys(newMarketplace).forEach(d => {
      newMarketplace[d].value = `${newMarketplace[d].name}_${newMarketplace[d].name}`;
    });
  }

  newMarketplace[key].value = `${key}_${value}`;
  return newMarketplace;
};

/**
 * verifyZoneTypeDataOnChange Returns true or false verifying the type of the value inserted
 * @param {string} type zone type to be validated (Numbers || Single letters|| Double letters || Triple letters)
 * @param {string} value value to be validated
 */
export const verifyZoneTypeDataOnChange = (type, value) => {
  if (!value) return true;
  if (typeof value === 'number') value = value.toString();

  switch (type) {
    case NUMBER_TYPE:
      return !!value.match(onlyNumberPattern);
    case S_LETTER_TYPE:
      return !!value.match(singleLetterPattern);
    case D_LETTER_TYPE:
      return value.match(singleLetterPattern) || value.match(doubleLetterPattern);
    case T_LETTER_TYPE:
      return value.match(singleLetterPattern) || value.match(doubleLetterPattern) || value.match(tripleLetterPattern);
    default:
      return false;
  }
};

/**
 * verifyZoneTypeData Returns true or false verifying the type of the value inserted
 * @param {string} type zone type to be validated (Numbers || Single letters|| Double letters || Triple letters)
 * @param {string} value value to be validated
 */
export const verifyZoneTypeData = (type, value) => {
  if (!value) return true;
  if (typeof value === 'number') value = value.toString();

  switch (type) {
    case NUMBER_TYPE:
      return !!value.match(onlyNumberPattern);
    case S_LETTER_TYPE:
      return !!value.match(singleLetterPattern);
    case D_LETTER_TYPE:
      return !!value.match(doubleLetterPattern);
    case T_LETTER_TYPE:
      return !!value.match(tripleLetterPattern);
    default:
      return false;
  }
};

/**
 * verifyZoneTypeRange Returns a formatted date with the description of the time has been passed or when it will happen
 * @param {string} type zone type to be validated (Numbers || Single letters|| Double letters || Triple letters)
 * @param {string} low value to be validated and should be lower than high
 * @param {string} high value to be validated and should be higher than low
 */
export const verifyZoneTypeRange = (type, low, high) => {
  if (!low || !high) return true;
  switch (type) {
    case NUMBER_TYPE:
      return Number(low) <= Number(high);
    case S_LETTER_TYPE:
    case D_LETTER_TYPE:
    case T_LETTER_TYPE:
      if (typeof low !== 'string') low = low.toString();
      if (typeof high !== 'string') high = high.toString();
      return low?.toLowerCase() <= high?.toLowerCase();
    default:
      return false;
  }
};

/**
 * formattedValue Returns a formatted date with the description of the time has been passed or when it will happen
 * @param {string} inputType type of the input to check if is not 'type'
 * @param {string} inputType zone type to be validated (Numbers || Single letters|| Double letters || Triple letters)
 * @param {string} value value to be formatted
 */
export const formattedValue = (inputType, zoneType, value) => {
  if (value === '') {
    return null;
  }
  if (!value) {
    return value;
  }
  if (inputType !== TYPE) {
    if (zoneType === NUMBER_TYPE) {
      return Number(value);
    }
    return value.toUpperCase();
  }
  return value;
};

/**
 * Get Strategy Name from Strategy Id
 * @param {array} tags Array of strategy tags
 * @param {tagId} Id of the selected tag
 */
export const getPricingStrategyTagNameFromId = (tags, tagId) => {
  const strategy = tags && tagId && Array.isArray(tags) && tags.find(t => t.strategy_id === tagId);
  return strategy ? strategy.strategy_name : null;
};

const groupsPositionSorter = (a, b) => {
  if (a.position < b.position) {
    return -1;
  }
  if (a.position > b.position) {
    return 1;
  }
  return 0;
};

/**
 * Get Final Groups based on changes from the server and current state session
 * @param {array} groups Array of groups fetched from the server
 * @param {array} currGroups Array of groups with information of current session
 * Function return the final groups that would be rendered in the UI, this allows to keep the current changes of the groups,
 * until user refreshes the page
 * Adding logic to handle group position (KS-1591)
 */
export const getFinalGroups = (groups, currGroups = []) => {
  const finalGroups = [...groups];
  let position = 0;
  let hasNullValue = false;
  const created = finalGroups.length > currGroups.length;
  finalGroups.forEach((elto, idx) => {
    const currIdx = currGroups.findIndex(curr => curr.id === elto.id);
    if (currIdx >= 0) {
      finalGroups[idx] = currGroups[currIdx];
      if (elto.id === currGroups[currIdx].id && elto.comp_all !== currGroups[currIdx].comp_all) {
        finalGroups[idx].comp_all = elto.comp_all;
      }
      if (
        finalGroups[idx].ranks.length !== groups[idx].ranks.length ||
        JSON.stringify(finalGroups[idx].ranks) !== JSON.stringify(groups[idx].ranks)
      ) {
        finalGroups[idx].ranks = groups[idx].ranks;
      }
      if (hasNullValue || created) {
        finalGroups[idx].position = currGroups[currIdx].position + 1;
      }
      if (finalGroups[idx].marketplace !== groups[idx].marketplace) {
        finalGroups[idx].marketplace = groups[idx].marketplace;
      }
      finalGroups[idx].last_date_change = groups[idx].last_date_change;
      finalGroups[idx].last_user_change = groups[idx].last_user_change;
      finalGroups[idx].internal_notes_validation = currGroups[currIdx].internal_notes_validation;
    } else if (elto.position === null) {
      hasNullValue = true;
      elto.position = position;
    }
    position += 1;
  });
  finalGroups.sort(groupsPositionSorter);
  return finalGroups;
};

/**
 * Get if a notification is already seen by the user
 * @param {Object} Notification Notification Object
 * @param {array} NewNotes Notes Unread by User
 */
export const isNotificationUnread = (notification = {}, newNotes = []) => {
  if (notification && newNotes.length > 0 && newNotes.includes(notification)) {
    return true;
  }
  return false;
};

/**
 * Get count of notification unread by the user
 * @param {Array} Notification Notification Array
 * @param {string} User User Object
 */
export const notificationsUnread = (notification = [], user) => {
  if (notification && notification.length > 0) {
    const unread = notification.filter(
      element => element.user_email !== user.email && element.users_read && !element.users_read.includes(user.email)
    );
    return unread;
  }
  return [];
};

export const setTempPreviousPosition = (groups = [], groupId) => {
  const finalGroups = [...groups];
  const groupIdx = groups.findIndex(g => g.id === groupId);
  delete finalGroups[groupIdx].previousPosition;
  return finalGroups;
};

export const setCompAllGroup = (groups = [], groupId, compAll) => {
  const finalGroups = [...groups];
  const groupIdx = groups.findIndex(g => g.id === groupId);
  finalGroups[groupIdx].comp_all = compAll;
  return finalGroups;
};

export const setSectionsGroup = (groups = [], groupId, typeListing, sections) => {
  const finalGroups = [...groups];
  const groupIdx = groups.findIndex(g => g.id === groupId);
  switch (typeListing) {
    case 'ticketnetwork':
      finalGroups[groupIdx].tnd_sections = sections;
      break;
    case 'seatgeek':
      finalGroups[groupIdx].sg_sections = sections;
      break;
    default:
      finalGroups[groupIdx].tnd_sections = sections;
      break;
  }
  return finalGroups;
};

export const setMarketplaceGroup = (groups = [], groupId, marketplace) => {
  const finalGroups = [...groups];
  const groupIdx = groups.findIndex(g => g.id === groupId);
  finalGroups[groupIdx].marketplace = [marketplace];
  return finalGroups;
};

export const setHashSectionsGroups = (groups = [], typeListing, sections) => {
  let finalGroups = [...groups];
  groups.forEach(g => {
    const currSections = (sections[typeListing] && sections[typeListing][g.id]) || [];
    finalGroups = setSectionsGroup(finalGroups, g.id, typeListing, currSections);
  });
  return finalGroups;
};

/**
 * Update groups array to update position of each one when there has been a change done via drag and drop
 * @param {Array} groups
 * @returns
 */

export const updateGroupsPosition = (groups = []) => {
  let position = 0;
  if (groups) {
    groups.forEach(elto => {
      elto.position = position;
      position += 1;
    });
  }
  return groups;
};

export const updateGroupsPositionFromBE = (groups = [], backendPositions = {}) => {
  if (groups) {
    groups.forEach(elto => {
      elto.position = backendPositions[String(elto.id)];
    });
  }
  return groups;
};

/**
 * Build object with groupid and position to call api
 * @param {Array} groups Array of groups
 * @returns
 */

export const getGroupsPosition = (groups = []) => {
  return groups.map(group => ({
    group_id: group.id,
    position: group.position,
    previous_position: group.previousPosition,
  }));
};

export const getPreviousGroupPosition = (groups = [], groupToSave) => {
  const groupsToSend = [];
  if ('previousPosition' in groupToSave) {
    groupsToSend.push(groupToSave);
    groupsToSend.push(groups[groupToSave.previousPosition]);
  }
  return groupsToSend;
};

function valueToNumber(value) {
  // Remove the dolar sign and convert the value on a float number
  if (typeof value === 'string') {
    const number = parseFloat(value.replace('$', ''));
    return number;
  }
  return value;
}

export const getBadGroupsData = (groups = []) => {
  const badGroups = [];
  if (groups && Array.isArray(groups) && groups.length > 0) {
    groups.forEach(group => {
      const { floor, ceiling, manual_price: manualPrice, increment, spaceout } = group;
      const groupFloor = valueToNumber(floor);
      const groupCeiling = valueToNumber(ceiling);
      if (
        !groupFloor ||
        (groupFloor && parseFloat(manualPrice) < parseFloat(groupFloor)) ||
        (groupCeiling && parseFloat(manualPrice) > parseFloat(groupCeiling)) ||
        (groupFloor && groupCeiling && parseFloat(groupFloor) > parseFloat(groupCeiling)) ||
        increment === '' ||
        spaceout === ''
      ) {
        badGroups.push(group);
      }
    });
  }
  return badGroups;
};

export const convertToArray = (splits, quantity = []) => {
  if (splits && !Array.isArray(splits)) {
    const splitJson = JSON.parse(splits);

    if (splitJson.length === 1 && splitJson[0] === '') {
      return [];
    }
    splitJson.forEach((item, index) => {
      if (parseInt(item, 10)) {
        splitJson[index] = parseInt(item, 10);
      } else {
        const replaced = item.replace(/\D/g, '');
        if (replaced) {
          splitJson[index] = parseInt(replaced, 10);
        }
      }
    });
    if (quantity && Array.isArray(quantity) && quantity.length > 0) {
      splitJson.forEach((item, idx) => {
        if (quantity.indexOf(item) === -1) {
          delete splitJson[idx];
        }
      });
    }
    return splitJson;
  }
  return splits;
};

const getMinTicketsQtyGeneralGroup = (group = {}, listings = []) => {
  const { ranks } = group;
  if (listings && Array.isArray(listings) && listings.length > 0) {
    const ranksListings = ranks.map(rank => {
      const filteredListings = listings.filter(l => l.skybox_id === rank.skybox_listing_id);
      return filteredListings[0];
    });
    const minQtyListingsGroup =
      ranksListings.length > 1
        ? ranksListings.reduce((prev, curr) => (prev.quantity < curr.quantity ? prev : curr))
        : ranks[0];
    return minQtyListingsGroup.quantity;
  }
  return 0;
};

export const getMinTicketsQty = (group = {}, listings = []) => {
  const autoBroadcast = group && group.auto_broadcast ? group.auto_broadcast : 0;
  const customSplits = [];
  const { ranks } = group;
  if (listings && Array.isArray(listings) && listings.length > 0 && autoBroadcast > 0) {
    const filteredRanks = ranks.slice(0, autoBroadcast);
    const ranksListings = filteredRanks.map(rank => {
      const filteredListings = listings.filter(l => l.skybox_id === rank.skybox_listing_id);
      return filteredListings[0];
    });
    const minQtyAutoBroadcast =
      ranksListings.length > 1
        ? ranksListings.reduce((prev, curr) => (prev.quantity < curr.quantity ? prev : curr))
        : ranksListings[0];
    customSplits.push(minQtyAutoBroadcast.quantity);
  }
  const minQtyListingsGroup = getMinTicketsQtyGeneralGroup(group, listings);
  if (minQtyListingsGroup > 0) {
    if (customSplits.length > 0) {
      const minAutoBroadcast = customSplits[0];
      if (minQtyListingsGroup !== minAutoBroadcast) {
        customSplits.push(minQtyListingsGroup);
      }
    } else {
      customSplits.push(minQtyListingsGroup);
    }
  }
  return customSplits;
};

export const getGroupMarketplaces = (groups = []) => {
  if (groups && Array.isArray(groups) && groups.length > 0) {
    const uniqueMarketplaces = [...new Set(groups.map(g => g))];
    return uniqueMarketplaces;
  }
  return [];
};

export const selectedDropdown = (marketplace, marketplaces = []) => {
  if (marketplaces) {
    return marketplaces.includes(marketplace);
  }
  return marketplace === DEFAULT_MARKETPLACE;
};

export const isActiveMarketplace = (marketplace, marketplaces = []) => {
  if (marketplaces && marketplaces.length > 0) {
    return marketplaces.length === 1 && marketplaces[0] === marketplace;
  }
  return true;
};

export const findSGZone = (zones, section) => {
  if (zones && Array.isArray(zones)) {
    const zone = zones.find(z => z.sections && z.sections.find(s => s.name.toUpperCase() === section.toUpperCase()));
    return zone;
  }
  return null;
};

const handleRowsSort = (listings = [], sort = 'asc') => {
  const numbers = listings
    .filter(l => !Number.isNaN(Number(l.rows)))
    .sort((a, b) => (sort === 'asc' ? Number(a.rows) - Number(b.rows) : Number(b.rows) - Number(a.rows)));

  const singleLetter = listings
    .filter(l => Number.isNaN(Number(l.rows)) && l.rows?.length === 1)
    .sort((a, b) => (sort === 'asc' ? a.rows.localeCompare(b.rows) : b.rows.localeCompare(a.rows)));

  const doubleLetter = listings
    .filter(l => Number.isNaN(Number(l.rows)) && l.rows?.length === 2)
    .sort((a, b) => (sort === 'asc' ? a.rows.localeCompare(b.rows) : b.rows.localeCompare(a.rows)));

  const tripleLetter = listings
    .filter(l => Number.isNaN(Number(l.rows)) && l.rows?.length === 3)
    .sort((a, b) => (sort === 'asc' ? a.rows.localeCompare(b.rows) : b.rows.localeCompare(a.rows)));

  return [...numbers, ...singleLetter, ...doubleLetter, ...tripleLetter];
};

export const gridSort = (rows, colId, sort) => {
  let sortedRows = [];
  if (colId === 'rows') {
    sortedRows = handleRowsSort(rows, sort);
  } else {
    if (colId === 'cost') {
      const listCostComparator = (a, b, attribute, ascending = true) => {
        const aNumber = parseFloat(a[attribute].replace('$', ''));
        const bNumber = parseFloat(b[attribute].replace('$', ''));

        return ascending ? aNumber - bNumber : bNumber - aNumber;
      };
      if (sort === 'asc') {
        return [...rows].sort((a, b) => listCostComparator(a, b, 'cost', true));
      }
      if (sort === 'desc') {
        return [...rows].sort((a, b) => listCostComparator(a, b, 'cost', false));
      }
    }

    sortedRows = rows.sort((a, b) => {
      const [firstRow] = rows;

      if (!Number.isNaN(Number(firstRow[colId]))) {
        if (sort === 'asc') return a[colId] - b[colId];
        return b[colId] - a[colId];
      }

      if (sort === 'asc') return a[colId].localeCompare(b[colId]);
      return b[colId].localeCompare(a[colId]);
    });
  }
  return sortedRows;
};

export const getGroupMarkets = (groupdId, marketplaces) => {
  if (marketplaces && marketplaces.groups_ids) {
    const marketInfoIndex = marketplaces.groups_ids.findIndex(element => element === groupdId);
    return marketplaces[marketInfoIndex] || {};
  }
  return {};
};

export const hasValues = (fields, fieldType, id = null) => {
  if (id) {
    const fieldsToCheck = fields[id] || [];
    if (fieldsToCheck) {
      return fields[id][fieldType] && fields[id][fieldType].length > 0;
    }
  }
  return Object.keys(fields).some(key => fields[key][fieldType] && fields[key][fieldType].length > 0);
};

export const validateIfCriteriaChanged = (groupFields, id, handleCriteriaChanges) => {
  const matching = Object.keys(groupFields).find(key => SAVE_ENABLE.find(s => groupFields[key].includes(s)));
  if (matching) {
    handleCriteriaChanges(id, true);
  } else {
    handleCriteriaChanges(id, false);
  }
};

export const saveGroupChanges = (
  id,
  fieldInfo,
  isPushEnable,
  field,
  groupFields,
  comingFrom = null,
  handleCriteriaChanges = null
) => {
  let newGroupFieldsToUpdate = {};
  let pushEnableUpdateValue = null;
  if (comingFrom) {
    field = comingFrom;
  }
  const { hasChanged, enablePush: isFieldPushSB } = fieldInfo;
  if (hasChanged) {
    if (id in groupFields) {
      const groupFieldsAdded = groupFields[id];
      let fieldsToCheck = [];
      let fieldType = '';
      if (isFieldPushSB) {
        fieldsToCheck = groupFieldsAdded.enablePush || [];
        fieldType = 'enablePush';
      } else {
        fieldsToCheck = groupFieldsAdded.normalFields || [];
        fieldType = 'normalFields';
      }

      if (!fieldsToCheck.includes(field)) {
        const newGroupFields = {
          ...groupFields,
          [id]: { ...groupFieldsAdded, [fieldType]: [...fieldsToCheck, field] },
        };
        newGroupFieldsToUpdate = newGroupFields;
        if (handleCriteriaChanges) {
          validateIfCriteriaChanged(newGroupFields[id][fieldType], id, handleCriteriaChanges);
        }
      }
    } else {
      let fieldType = '';
      if (isFieldPushSB) {
        fieldType = 'enablePush';
      } else {
        fieldType = 'normalFields';
      }
      const newGroupFields = { ...groupFields, [id]: { [fieldType]: [field] } };
      newGroupFieldsToUpdate = newGroupFields;
      if (handleCriteriaChanges) {
        validateIfCriteriaChanged(newGroupFields[id][fieldType], id, handleCriteriaChanges);
      }
    }
    if (isPushEnable !== hasChanged && isFieldPushSB) {
      pushEnableUpdateValue = hasChanged;
    }
  } else {
    if (id in groupFields) {
      const groupFieldsAdded = groupFields[id];
      let fieldsToCheck = [];
      let fieldType = '';
      if (isFieldPushSB) {
        fieldsToCheck = groupFieldsAdded.enablePush || [];
        fieldType = 'enablePush';
      } else {
        fieldsToCheck = groupFieldsAdded.normalFields || [];
        fieldType = 'normalFields';
      }
      if (fieldsToCheck.includes(field)) {
        const idx = fieldsToCheck.indexOf(field);
        fieldsToCheck.splice(idx, 1);
        const newGroupFields = { ...groupFields, [id]: { ...groupFieldsAdded, [fieldType]: fieldsToCheck } };
        newGroupFieldsToUpdate = newGroupFields;
        if (handleCriteriaChanges) {
          validateIfCriteriaChanged(newGroupFields[id][fieldType], id, handleCriteriaChanges);
        }
      }
    }

    const isEnabled = hasValues(groupFields, 'enablePush');
    if (isEnabled !== isPushEnable) {
      pushEnableUpdateValue = isEnabled;
    }
  }
  return { newGroupFieldsToUpdate, pushEnableUpdateValue };
};

const sortSections = (a, b) => {
  if (a.canonical_name > b.canonical_name) {
    return 1;
  }
  if (a.canonical_name < b.canonical_name) {
    return -1;
  }
  return 0;
};

export const convertCompsToCompare = comps => {
  return comps
    .map(f => ({
      canonical_name: f.canonical_name,
      section_name: f.section_name,
      zone_name: f.zone_name,
    }))
    .sort(sortSections);
};

const isValueChanged = (initValue, currentValue) => {
  if (Array.isArray(initValue) || Array.isArray(currentValue)) {
    return !(initValue.length === currentValue.length) || !(JSON.stringify(initValue) === JSON.stringify(currentValue));
  }
  if (!Number.isNaN(Number(initValue)) && !Number.isNaN(Number(currentValue))) {
    return !(formatFloat(initValue) === formatFloat(currentValue));
  }
  return !(initValue === currentValue);
};

export const validateGroupChanges = (initialGroupState, currentState, field) => {
  const initValue = (initialGroupState && field in initialGroupState && initialGroupState[field]) || null;
  const currentValue = (currentState && field in currentState && currentState[field]) || null;
  if (PUSH_TO_SB_ENABLE.includes(field)) {
    return { enablePush: true, hasChanged: isValueChanged(initValue, currentValue) };
  }
  return { enablePush: false, hasChanged: isValueChanged(initValue, currentValue) };
};

/**
 * Safely parse JSON value, handling double-stringified JSON strings
 * @returns {array} Parsed array or empty array if parsing fails
 */
const safeParseBroadcastingStrategy = value => {
  if (!value) return [];

  // If already an array, return it
  if (Array.isArray(value)) return value;

  // If it's a string, attempt to parse it
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);

      // If the parsed result is still a string, it was double-stringified
      // Parse it again
      if (typeof parsed === 'string') {
        return JSON.parse(parsed);
      }

      return parsed;
    } catch (error) {
      // If parsing fails, return empty array
      return [];
    }
  }

  return [];
};

export const getMarketplaces = _groups => {
  const currMarketplaces = marketplacesAPI.response.ranks.reduce((prev, current) => {
    // eslint-disable-next-line no-param-reassign
    prev[current.name] = {
      ignorePhrase: current.ignorePhrase,
      includePhrase: current.includePhrase,
      name: current.name,
      value: `${current.name}_${current.name}`,
    };
    return prev;
  }, {});

  const baseMarketplaces = cloneDeep(currMarketplaces);
  const newMarketplaces = [cloneDeep(currMarketplaces)];

  const hash = {};
  Object.keys(newMarketplaces[0]).forEach(key => {
    hash[newMarketplaces[0][key].ignorePhrase] = key;
    hash[newMarketplaces[0][key].includePhrase] = key;
  });

  const groupsIds = [];

  _groups.forEach((group, index) => {
    try {
      const broadcastingStrategy =
        (group.broadcasting_strategy && safeParseBroadcastingStrategy(group.broadcasting_strategy)) || [];
      const includeExclude = broadcastingStrategy;
      const ranks = includeExclude.map(r => ({
        key: hash[r],
        value: `${hash[r]}_${r}`,
      }));
      newMarketplaces[index] = cloneDeep(baseMarketplaces);
      ranks.forEach(({ key, value }) => {
        try {
          newMarketplaces[index][key].value = value;
        } catch (e) {
          console.error('Error updating marketplace value for group', group.id, e);
        }
      });
      groupsIds.push(group.id);
    } catch (e) {
      console.error('Error parsing broadcasting_strategy for group', group.id, e);
      newMarketplaces[index] = cloneDeep(baseMarketplaces);
      const includeExclude = [];
      const ranks = includeExclude.map(r => ({
        key: hash[r],
        value: `${hash[r]}_${r}`,
      }));
      ranks.forEach(({ key, value }) => {
        newMarketplaces[index][key].value = value;
      });
      groupsIds.push(group.id);
    }
  });

  newMarketplaces.groups_ids = groupsIds;
  return newMarketplaces;
};

export default {
  onlyNumbers,
  formatFloat,
  handleMarketplace,
};
